import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './DemoVideos.css';

const DemoVideos = () => {
  const scrollContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Video data - easy to add more videos
  const videos = [
    {
      id: 1,
      anchorId: 'demo-healing',
      slug: 'ai-test-healing',
      src: '/testhealing.mp4',
      title: 'AI Test Healing',
      description: <>Watch the agent analyze a test failure and suggest healing adaptations &mdash; <strong className="demo-emphasis">no manual effort required</strong>.</>,
      duration: '2:15'
    },
    {
      id: 2,
      anchorId: 'demo-bugfix',
      slug: 'ai-bug-fixing',
      src: '/bugfixing.mp4',
      title: 'AI Bug Fixing',
      description: <>See the agent detect a real bug and <strong className="demo-emphasis">suggest the code fix</strong> to resolve it.</>,
      duration: '1:45'
    },
  ];

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth * 0.85;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(newIndex, videos.length - 1));
    }
  };

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.offsetWidth * 0.85;
      container.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  return (
    <section className="demo-videos-section" id="demo">
      <div className="demo-videos-container">
        <div className="demo-videos-header">
          <h2 className="demo-videos-title">See it in action</h2>
          <p className="demo-videos-subtitle">
            Real footage of the agent at work &mdash; healing tests and fixing bugs, with you in the loop.
          </p>
        </div>

        <div className="demo-videos-wrapper">
          {/* Playful hand-drawn callouts framing the live footage */}
          <div className="demo-callout demo-callout-left">
            <span className="demo-callout-text">AI you can talk to, not hidden away</span>
            <svg className="demo-callout-arrow" viewBox="0 0 60 50" fill="none">
              <path d="M5 10 Q15 15 25 25 Q35 35 45 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d="M40 48 L47 47 L48 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>

          <div className="demo-callout demo-callout-right">
            <svg className="demo-callout-arrow" viewBox="0 0 60 50" fill="none">
              <path d="M55 10 Q45 15 35 25 Q25 35 15 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d="M20 48 L13 47 L12 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <span className="demo-callout-text">real, live app footage</span>
          </div>

          <div
            className="demo-videos-scroll-container"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            {videos.map((video, index) => (
              <div key={video.id} id={video.anchorId} className="demo-video-card">
                <div className="demo-video-wrapper">
                  <video
                    className="demo-video-player"
                    controls
                    preload="metadata"
                  >
                    {/* #t=0.1 makes the browser paint the first frame as a poster
                        (with preload=none it shows a black box until play) */}
                    <source src={`${video.src}#t=0.1`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="demo-video-duration">{video.duration}</div>
                </div>
                <div className="demo-video-info">
                  <h3 className="demo-video-title">{video.title}</h3>
                  <p className="demo-video-description">{video.description}</p>
                  {video.slug && (
                    <Link to={`/demos/${video.slug}`} className="demo-video-link">
                      Watch full demo
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot indicators - only show if more than 1 video */}
        {videos.length > 1 && (
          <div className="demo-videos-dots">
            {videos.map((_, index) => (
              <button
                key={index}
                className={`demo-dot ${index === activeIndex ? 'active' : ''}`}
                onClick={() => scrollToIndex(index)}
                aria-label={`Go to video ${index + 1}`}
              />
            ))}
          </div>
        )}

        <div className="demo-videos-all">
          <Link to="/demos" className="demo-videos-all-link">
            View all demos
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DemoVideos;
